import { useEffect, useMemo, useState } from "react";
import api, { apiError, formatEuro, imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ImageUpload } from "@/components/ImageUpload";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, PencilSimple, Trash, X } from "@phosphor-icons/react";

const CATEGORIE = ["Antipasto", "Piatto principale", "Contorno", "Dolce", "Bevanda"];
const emptyForm = { nome: "", descrizione: "", categoria: "Piatto principale", prezzo: "", attivo: true, ricetta: [], foto: null };

export default function Piatti() {
  const [dishes, setDishes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const { user } = useAuth();
  const isAdmin = ["admin", "master"].includes(user?.role);

  const load = () => {
    api.get("/dishes").then((r) => setDishes(r.data)).catch(() => {});
    api.get("/ingredients").then((r) => setIngredients(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const ingMap = useMemo(() => Object.fromEntries(ingredients.map((i) => [i.id, i])), [ingredients]);

  const preview = useMemo(() => {
    const tot = { kcal: 0, proteine: 0, carboidrati: 0, grassi: 0 };
    let costo = 0;
    for (const item of form.ricetta) {
      const ing = ingMap[item.ingredient_id];
      const q = parseFloat(item.quantita) || 0;
      if (!ing) continue;
      const factor = ing.unita === "pz" ? q : q / 100;
      tot.kcal += ing.nutrizione.kcal * factor;
      tot.proteine += ing.nutrizione.proteine * factor;
      tot.carboidrati += ing.nutrizione.carboidrati * factor;
      tot.grassi += ing.nutrizione.grassi * factor;
      costo += ing.costo_per_unita * factor;
    }
    return { ...Object.fromEntries(Object.entries(tot).map(([k, v]) => [k, Math.round(v * 10) / 10])), costo: Math.round(costo * 100) / 100 };
  }, [form.ricetta, ingMap]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      nome: d.nome, descrizione: d.descrizione, categoria: d.categoria, prezzo: String(d.prezzo), attivo: d.attivo, foto: d.foto || null,
      ricetta: d.ricetta.map((r) => ({ ingredient_id: r.ingredient_id, quantita: String(r.quantita) })),
    });
    setOpen(true);
  };

  const addRow = () => setForm((f) => ({ ...f, ricetta: [...f.ricetta, { ingredient_id: "", quantita: "100" }] }));
  const setRow = (idx, key, val) => setForm((f) => ({ ...f, ricetta: f.ricetta.map((r, i) => (i === idx ? { ...r, [key]: val } : r)) }));
  const removeRow = (idx) => setForm((f) => ({ ...f, ricetta: f.ricetta.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.nome) { toast.error("Il nome è obbligatorio"); return; }
    const payload = {
      nome: form.nome, descrizione: form.descrizione, categoria: form.categoria,
      prezzo: parseFloat(form.prezzo) || 0, attivo: form.attivo, foto: form.foto,
      ricetta: form.ricetta.filter((r) => r.ingredient_id).map((r) => ({ ingredient_id: r.ingredient_id, quantita: parseFloat(r.quantita) || 0 })),
    };
    try {
      if (editing) { await api.put(`/dishes/${editing.id}`, payload); toast.success("Piatto aggiornato"); }
      else { await api.post("/dishes", payload); toast.success("Piatto creato"); }
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Eliminare "${d.nome}"?`)) return;
    try { await api.delete(`/dishes/${d.id}`); toast.success("Piatto eliminato"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="piatti-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold">Piatti</h1>
          <p className="text-[#6B7280] mt-1">Ricette con calcolo automatico di nutrizione e food cost.</p>
        </div>
        {isAdmin && (
          <Button onClick={openNew} data-testid="dish-add-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F] gap-2">
            <Plus size={18} weight="bold" /> Nuovo piatto
          </Button>
        )}
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
        <Table data-testid="dishes-table">
          <TableHeader className="bg-[#FAF9F6]">
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>Prezzo</TableHead>
              <TableHead>Food cost</TableHead>
              <TableHead>Margine</TableHead>
              <TableHead>Kcal</TableHead>
              <TableHead>P / C / G</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dishes.map((d) => (
              <TableRow key={d.id} className="hover:bg-stone-50" data-testid={`dish-row-${d.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {d.foto ? (
                      <img src={imageUrl(d.foto)} alt={d.nome} className="h-10 w-10 rounded-lg object-cover border border-[#E5E5E0]" />
                    ) : (
                      <div className="h-10 w-10 rounded-lg bg-[#FAF9F6] border border-[#E5E5E0]" />
                    )}
                    <div>
                      <p className="font-medium">{d.nome}</p>
                      <p className="text-xs text-[#6B7280] max-w-[220px] truncate">{d.descrizione}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell><Badge variant="outline" className="rounded-full">{d.categoria}</Badge></TableCell>
                <TableCell className="font-semibold">{formatEuro(d.prezzo)}</TableCell>
                <TableCell>{formatEuro(d.costo_ingredienti)}</TableCell>
                <TableCell className={d.margine >= 0 ? "text-green-700" : "text-red-700"}>{formatEuro(d.margine)}</TableCell>
                <TableCell className="font-medium">{d.nutrizione.kcal}</TableCell>
                <TableCell className="text-xs text-[#6B7280]">{d.nutrizione.proteine}g / {d.nutrizione.carboidrati}g / {d.nutrizione.grassi}g</TableCell>
                <TableCell>
                  <Badge className={`rounded-full ${d.attivo ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-stone-100 text-stone-600 hover:bg-stone-100"}`}>
                    {d.attivo ? "Attivo" : "Non attivo"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {isAdmin && (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(d)} data-testid={`dish-edit-${d.id}`} className="hover:bg-stone-100"><PencilSimple size={18} /></Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(d)} data-testid={`dish-delete-${d.id}`} className="hover:bg-red-50 text-[#D32F2F]"><Trash size={18} /></Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dish-dialog">
          <DialogHeader><DialogTitle>{editing ? "Modifica piatto" : "Nuovo piatto"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Nome</Label>
                <Input value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} data-testid="dish-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Categoria</Label>
                <Select value={form.categoria} onValueChange={(v) => setForm((f) => ({ ...f, categoria: v }))}>
                  <SelectTrigger data-testid="dish-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIE.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Descrizione</Label>
              <Input value={form.descrizione} onChange={(e) => setForm((f) => ({ ...f, descrizione: e.target.value }))} data-testid="dish-description-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Foto</Label>
              <ImageUpload value={form.foto} onChange={(p) => setForm((f) => ({ ...f, foto: p }))} testId="dish-photo" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
              <div className="space-y-1.5">
                <Label>Prezzo (€)</Label>
                <Input type="number" step="0.10" value={form.prezzo} onChange={(e) => setForm((f) => ({ ...f, prezzo: e.target.value }))} data-testid="dish-price-input" />
              </div>
              <div className="flex items-center gap-3 pb-2">
                <Switch checked={form.attivo} onCheckedChange={(v) => setForm((f) => ({ ...f, attivo: v }))} data-testid="dish-active-switch" />
                <Label>Attivo nel menu</Label>
              </div>
            </div>

            <div className="border border-[#E5E5E0] rounded-xl p-4 space-y-3 bg-[#FAF9F6]">
              <div className="flex items-center justify-between">
                <p className="font-semibold">Ricetta</p>
                <Button variant="outline" size="sm" onClick={addRow} data-testid="dish-add-ingredient-button" className="rounded-full gap-1.5 hover:bg-white">
                  <Plus size={14} /> Aggiungi ingrediente
                </Button>
              </div>
              {form.ricetta.length === 0 && <p className="text-sm text-[#6B7280]">Nessun ingrediente. Aggiungine uno per calcolare i valori nutrizionali.</p>}
              {form.ricetta.map((row, idx) => {
                const ing = ingMap[row.ingredient_id];
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <Select value={row.ingredient_id} onValueChange={(v) => setRow(idx, "ingredient_id", v)}>
                      <SelectTrigger className="flex-1 bg-white" data-testid={`dish-recipe-ingredient-${idx}`}><SelectValue placeholder="Seleziona ingrediente" /></SelectTrigger>
                      <SelectContent>{ingredients.map((i) => <SelectItem key={i.id} value={i.id}>{i.nome} ({i.categoria})</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" step="1" className="w-24 bg-white" value={row.quantita} onChange={(e) => setRow(idx, "quantita", e.target.value)} data-testid={`dish-recipe-qty-${idx}`} />
                    <span className="text-sm text-[#6B7280] w-6">{ing ? ing.unita : "g"}</span>
                    <Button variant="ghost" size="icon" onClick={() => removeRow(idx)} data-testid={`dish-recipe-remove-${idx}`} className="text-[#D32F2F] hover:bg-red-50"><X size={16} /></Button>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-5 gap-3 text-center" data-testid="dish-nutrition-preview">
              {[
                ["Kcal", preview.kcal], ["Proteine", `${preview.proteine}g`], ["Carboidrati", `${preview.carboidrati}g`],
                ["Grassi", `${preview.grassi}g`], ["Food cost", formatEuro(preview.costo)],
              ].map(([label, val]) => (
                <div key={label} className="bg-white border border-[#E5E5E0] rounded-xl py-3">
                  <p className="text-lg font-bold" style={{ fontFamily: "Manrope" }}>{val}</p>
                  <p className="text-xs text-[#6B7280]">{label}</p>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Annulla</Button>
            <Button onClick={save} data-testid="dish-save-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F]">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
